// Copyright (c) 2026 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import type { JSX } from "react";

import {
  ConditionBlockType,
  ConditionBlockTimeMode,
  ICondition,
  IConditionBlock,
  IConditionBlockCalendarRange,
  IConditionBlockComment,
  IConditionBlockWeekdayRange,
  IParameter,
  ReferenceCompareType,
  ReferenceConstant,
  ReferenceType,
} from "shared/types/parameter";
import { isSet, round } from "shared/utils/utils";

import IconComment from "icons/cond/condComment";
import IconConstant from "icons/cond/condConstant";
import IconCalendar from "icons/cond/condCalendar";
import IconTime from "icons/cond/condTime";
import IconWeek from "icons/cond/condWeek";
import IconPriority from "icons/cond/condPriority";
import IconMinoff from "icons/cond/condMinoff";
import IconMaxoff from "icons/cond/condMaxoff";
import IconMinon from "icons/cond/condMinon";
import IconMaxon from "icons/cond/condMaxon";
import IconPwm from "icons/cond/condPwm";
import IconInput from "icons/cond/condInput";
import IconCondition from "icons/cond/condCondition";
import IconCompare from "icons/cond/condCompare";
import IconSequence from "icons/cond/condSequence";
import IconError from "icons/cond/condError";
import IconPower from "icons/cond/condPower";
import IconEpex from "icons/cond/condEpex";
import IconStatistic from "icons/cond/condStatistic";
import IconWeather from "icons/cond/condWeather";
import IconPid from "icons/cond/condPid";
import IconFormula from "icons/cond/condFormula";
import IconCurve from "icons/cond/condCurve";
import IconTimeReference from "icons/cond/condTimeReference";
import IconStart from "icons/cond/condStart";
import IconEnd from "icons/cond/condEnd";

interface IConditionGraphProps {
  condition: ICondition;
  parameter: IParameter | undefined;
  // true while the live view is selected; without it the graph renders exactly as before,
  // regardless of whether live data happens to be present
  showLive?: boolean;
  // live per-block result (NV_CondBlockStatus), engine-computed, one entry per
  // condition.blocks[i]; also the weight the block contributes to its outgoing graph edges
  blockResult?: number[];
  // live per-block data value (NV_CondBlockValues), the block's underlying measured quantity
  // (e.g. the compared value of a COMPARE block); not meaningful for every block type, see
  // NO_LIVE_CAPTION_TYPES
  dataValue?: number[];
  // live path weight per graph node, engine order [start, ...blocks, end]; a non-zero value
  // marks the node as part of the currently active path from start to end (NV_CondBlockConStatus)
  connectionStatus?: number[];
}

// grid layout matching the enplan condition editor, so positions line up the same way
const GRID_SIZE_X = 102;
const GRID_SIZE_Y = 58;
const ICON_SIZE = 48;
// comment blocks have no icon and wrap their text across roughly three grid cells
const COMMENT_WIDTH = 3 * GRID_SIZE_X;
// height of the status border: the full grid row plus a bit, so it covers the caption/value
// shown below the icon in live view with some breathing room past the text
const BODY_HEIGHT = GRID_SIZE_Y + 6;
// the status border is a bit wider than the icon itself (and centered on it), purely cosmetic
const BODY_WIDTH = ICON_SIZE + 8;
const BODY_OFFSET = (ICON_SIZE - BODY_WIDTH) / 2;
// connecting lines touch the border's actual left/right edge
const BODY_RIGHT_EDGE = BODY_OFFSET + BODY_WIDTH;
// the CSS outline used for the status border (conditiongraph-status-on/off in content.scss)
// draws this far past the border box: outline-width (2px) + outline-offset (1px)
const OUTLINE_REACH = 4;
// the border (including its outline) extends left of the icon's own grid position; the whole
// canvas is shifted right by this much so that overhang never renders at a negative position,
// where it would get clipped by the scrollable page container instead of just being visible
const CANVAS_MARGIN = -BODY_OFFSET + OUTLINE_REACH;
// extra gap between the border and the final condition value shown beside the end node
const END_VALUE_GAP = 16;

// a node of the rendered graph; the start/end nodes are synthesized (no entry in blocks[])
interface IGraphNode {
  type: ConditionBlockType;
  position: number;
  block?: IConditionBlock;
}

function decodePosition(position: number) {
  return {
    x: (position >> 4) & 0x0f,
    y: position & 0x0f,
  };
}

const BLOCK_ICONS: Partial<
  Record<ConditionBlockType, React.FunctionComponent<React.SVGProps<SVGSVGElement>>>
> = {
  [ConditionBlockType.FALSE]: IconComment,
  [ConditionBlockType.CONSTANT]: IconConstant,
  [ConditionBlockType.CALENDAR]: IconCalendar,
  [ConditionBlockType.DAYTIME]: IconTime,
  [ConditionBlockType.WEEKDAY]: IconWeek,
  [ConditionBlockType.PRIORITY]: IconPriority,
  [ConditionBlockType.MINOFF]: IconMinoff,
  [ConditionBlockType.MAXOFF]: IconMaxoff,
  [ConditionBlockType.MINON]: IconMinon,
  [ConditionBlockType.MAXON]: IconMaxon,
  [ConditionBlockType.PWM]: IconPwm,
  [ConditionBlockType.VALUE]: IconInput,
  [ConditionBlockType.CONDITION]: IconCondition,
  [ConditionBlockType.COMPARE]: IconCompare,
  [ConditionBlockType.SEQUENCE]: IconSequence,
  [ConditionBlockType.ERROR]: IconError,
  [ConditionBlockType.POWER]: IconPower,
  [ConditionBlockType.EPEX]: IconEpex,
  [ConditionBlockType.STATISTIC]: IconStatistic,
  [ConditionBlockType.WEATHER]: IconWeather,
  [ConditionBlockType.PID]: IconPid,
  [ConditionBlockType.TIMEREFERENCE]: IconTimeReference,
  [ConditionBlockType.FORMULA]: IconFormula,
  [ConditionBlockType.CURVE]: IconCurve,
  [ConditionBlockType.START]: IconStart,
  [ConditionBlockType.END]: IconEnd,
};

// generic placeholder for block types without a dedicated icon
function IconPlaceholder(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 48 48" {...props}>
      <rect x="2" y="2" width="44" height="44" fill="none" stroke="#ccc" strokeDasharray="4" />
    </svg>
  );
}

function getBlockIcon(
  type: ConditionBlockType,
): React.FunctionComponent<React.SVGProps<SVGSVGElement>> {
  return BLOCK_ICONS[type] ?? IconPlaceholder;
}

const COMPARE_OPERATORS: Record<ReferenceCompareType, string> = {
  [ReferenceCompareType.GT]: ">",
  [ReferenceCompareType.GE]: "≥",
  [ReferenceCompareType.LT]: "<",
  [ReferenceCompareType.LE]: "≤",
  [ReferenceCompareType.EQ]: "=",
  [ReferenceCompareType.NE]: "≠",
  [ReferenceCompareType.BITWISE_AND]: "&",
};

// short names for the special "derived constant" references (ReferenceType.CONSTANT_DERIVED)
const CONSTANT_NAMES: Partial<Record<ReferenceConstant, string>> = {
  [ReferenceConstant.DATE]: "date",
  [ReferenceConstant.TIME]: "time",
  [ReferenceConstant.TIMESTAMP]: "ts",
  [ReferenceConstant.EPEX_PRICE]: "EPEX",
  [ReferenceConstant.TEMPERATURE]: "temp",
};

type IReference = { type: ReferenceType; id: number | null };

function getReferenceListForType(parameter: IParameter | undefined, type: ReferenceType) {
  switch (type) {
    case ReferenceType.INPUT_VALUE:
      return parameter?.input;
    case ReferenceType.OUTPUT_VALUE:
      return parameter?.output;
    case ReferenceType.CONDITION_VALUE:
      return parameter?.condition;
    case ReferenceType.MODULE_ERROR:
      return parameter?.module;
    case ReferenceType.ENERGY_METER_ENERGY:
      return parameter?.energymeter;
    case ReferenceType.TARIFF_CIRCUIT_POWER:
      return parameter?.tariffline;
    case ReferenceType.REGULATION_TARGET_VALUE:
      return parameter?.regulation;
    case ReferenceType.CONSTANT_DERIVED:
      return undefined;
  }
}

// references address items by their id field (falling back to the array index), not by index
function resolveItemName(
  ref: IReference | undefined,
  parameter: IParameter | undefined,
): string | undefined {
  if (!isSet(ref) || ref.id === null) {
    return undefined;
  }
  const list = getReferenceListForType(parameter, ref.type);
  return list?.find((item, index) => (item.id ?? index) === ref.id)?.name;
}

// full reference label (used by enplan for value/error blocks); derived constants use their name
function getLongRefText(
  ref: IReference | undefined,
  parameter: IParameter | undefined,
  showZero: boolean,
): string {
  if (!isSet(ref)) {
    return "";
  }
  if (ref.type === ReferenceType.CONSTANT_DERIVED) {
    return CONSTANT_NAMES[ref.id as ReferenceConstant] ?? (showZero ? "0" : "");
  }
  return resolveItemName(ref, parameter) ?? "";
}

// short reference label, mirroring enplan: parameter names are truncated to 5 chars,
// derived constants use their special short name; showZero controls the empty/zero fallback
function getShortRefText(
  ref: IReference | undefined,
  parameter: IParameter | undefined,
  showZero: boolean,
): string {
  if (isSet(ref) && ref.type === ReferenceType.CONSTANT_DERIVED) {
    return CONSTANT_NAMES[ref.id as ReferenceConstant] ?? (showZero ? "0" : "");
  }
  return resolveItemName(ref, parameter)?.slice(0, 5) ?? "";
}

function getOffsetString(offset: number, showPositiveSign: boolean): string {
  if (offset > 0) {
    return `${showPositiveSign ? "+" : ""}${round(offset, 6)}`;
  }
  if (offset < 0) {
    return `-${round(-offset, 6)}`;
  }
  return "";
}

function getRegulationLevelText(level: number): string {
  if (level === 0) {
    return "OFF";
  }
  if (level <= 10) {
    return `S${level}`;
  }
  if (level <= 20) {
    return `T${level - 10}`;
  }
  if (level === 22) {
    return "integral";
  }
  if (level === 23) {
    return "tendency";
  }
  return "ON";
}

const STATISTIC_OPERATORS: Record<string, string> = {
  min: "Minimum",
  max: "Maximum",
  sum: "Sum",
  avg: "Average",
  count_greater: "Count >",
  count_smaller: "Count <",
};

function getMonthName(month: number): string {
  return new Date(2010, month - 1, 1).toLocaleString(undefined, { month: "short" });
}

function getWeekdayName(weekday: number): string {
  // weekday 1 = Monday ... 7 = Sunday
  return new Date(2018, 0, 1 + ((weekday + 6) % 7)).toLocaleString(undefined, { weekday: "short" });
}

function formatSeconds(seconds: number): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  return hours > 0 ? `${pad(hours)}:${pad(minutes)}:${pad(secs)}` : `${pad(minutes)}:${pad(secs)}`;
}

// same as formatSeconds but never shows an hour component, mirroring enplan's live MINOFF/MINON
// display (unlike MAXOFF/MAXON/PWM, which do show hours for durations over an hour)
function formatMinutesSeconds(seconds: number): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(Math.floor(seconds / 60))}:${pad(Math.floor(seconds % 60))}`;
}

// caption describing the block's configuration values (no live data); mirrors the
// short text of the enplan condition editor
function getBlockCaption(block: IConditionBlock, parameter: IParameter | undefined): string {
  switch (block.type) {
    case ConditionBlockType.COMPARE: {
      const cmp = block as IConditionBlock & {
        compareOperator?: ReferenceCompareType;
        offset?: number;
        references?: IReference[];
      };
      const op = COMPARE_OPERATORS[cmp.compareOperator ?? ReferenceCompareType.GT];
      const ref0 = getShortRefText(cmp.references?.[0], parameter, true);
      const offset = cmp.offset ?? 0;
      // compare against a plain constant when the second reference is empty/zero
      if (getShortRefText(cmp.references?.[1], parameter, false) === "") {
        return `${ref0}${op}${offset ? getOffsetString(offset, false) : "0"}`;
      }
      const ref1 = getShortRefText(cmp.references?.[1], parameter, false);
      return `${ref0}${op}${ref1}${getOffsetString(offset, true)}`;
    }
    case ConditionBlockType.VALUE: {
      // value blocks display the full reference name plus offset (enplan uses the long text here)
      const value = block as IConditionBlock & {
        offset?: number;
        reciprocal?: boolean;
        references?: IReference[];
      };
      const refText = getLongRefText(value.references?.[0], parameter, true);
      if (refText === "") {
        return "";
      }
      let text = `${refText}${getOffsetString(value.offset ?? 0, true)}`;
      if (value.reciprocal) {
        text = `1/${text}`;
      }
      return text;
    }
    case ConditionBlockType.CONSTANT:
      return isSet((block as { value?: number }).value) ?
          String(round((block as { value?: number }).value, 6))
        : "";
    case ConditionBlockType.PWM: {
      const pwm = block as { onTime?: number; offTime?: number };
      return `${pwm.onTime ?? "?"}:${pwm.offTime ?? "?"}`;
    }
    case ConditionBlockType.PRIORITY:
      return (block as { levels?: number[] }).levels?.map(getRegulationLevelText).join(",") ?? "";
    case ConditionBlockType.MINOFF:
      return formatSeconds((block as { minOffTime?: number }).minOffTime ?? 0);
    case ConditionBlockType.MINON:
      return formatSeconds((block as { minOnTime?: number }).minOnTime ?? 0);
    case ConditionBlockType.MAXOFF:
      return formatSeconds((block as { maxOffTime?: number }).maxOffTime ?? 0);
    case ConditionBlockType.MAXON:
      return formatSeconds((block as { maxOnTime?: number }).maxOnTime ?? 0);
    case ConditionBlockType.SEQUENCE: {
      // active period numbers (1-based), e.g. "1,3"
      const periods = (block as { periods?: boolean[] }).periods ?? [];
      const active = periods
        .map((isActive, index) => (isActive ? index + 1 : undefined))
        .filter(isSet);
      return active.join(",");
    }
    case ConditionBlockType.CALENDAR: {
      const ranges = (block as { ranges?: IConditionBlockCalendarRange[] }).ranges ?? [];
      return formatCalendarRanges(ranges);
    }
    case ConditionBlockType.DAYTIME: {
      const daytime = block as {
        ranges?: { startTime: number; endTime: number }[];
        startMode?: ConditionBlockTimeMode;
        endMode?: ConditionBlockTimeMode;
      };
      return formatDaytime(daytime.ranges ?? [], daytime.startMode, daytime.endMode);
    }
    case ConditionBlockType.WEEKDAY: {
      const ranges = (block as { ranges?: IConditionBlockWeekdayRange[] }).ranges ?? [];
      return formatWeekdayRanges(ranges);
    }
    case ConditionBlockType.POWER:
      return `${round((block as { power?: number }).power, 6) ?? "?"} kW`;
    case ConditionBlockType.PID: {
      // first reference (controlled value) -> second reference / setpoint
      const pid = block as {
        setpointOffset?: number;
        references?: IReference[];
      };
      const ref0 = getShortRefText(pid.references?.[0], parameter, true);
      const ref1 =
        pid.references?.[1]?.type === ReferenceType.CONSTANT_DERIVED ?
          String(round(pid.setpointOffset ?? 0, 6))
        : getShortRefText(pid.references?.[1], parameter, false);
      return `${ref0}→${ref1}`;
    }
    case ConditionBlockType.FORMULA: {
      // first expression line, typically a "#" comment naming the result
      const expression = (block as { expression?: string }).expression?.trim() ?? "";
      return expression.split("\n")[0].replace(/^#\s*/, "").trim() || "<not set>";
    }
    case ConditionBlockType.CURVE:
      return `f(${getShortRefText((block as { references?: IReference[] }).references?.[0], parameter, false)})`;
    case ConditionBlockType.STATISTIC: {
      const stat = block as { operation?: string; limit?: number };
      const operator = STATISTIC_OPERATORS[stat.operation ?? ""] ?? stat.operation ?? "";
      return stat.operation === "count_greater" || stat.operation === "count_smaller" ?
          `${operator} ${stat.limit ?? ""}`
        : operator;
    }
    case ConditionBlockType.WEATHER: {
      const weather = block as {
        value?: string;
        observationOffset?: number;
        references?: IReference[];
      };
      const valueText = weather.value === "sunhours" ? "sun" : "temp";
      const obs =
        getShortRefText(weather.references?.[0], parameter, false) ||
        `${round(weather.observationOffset, 6) ?? 0}`;
      return `${valueText} ${obs} h`;
    }
    case ConditionBlockType.EPEX: {
      const epex = block as {
        observationOffset?: number;
        limitOffset?: number;
        references?: IReference[];
      };
      const obs =
        getShortRefText(epex.references?.[0], parameter, false) ||
        `${round(epex.observationOffset, 6) ?? 0}`;
      const limitRef = getShortRefText(epex.references?.[1], parameter, false);
      const limit =
        isSet(epex.limitOffset) && epex.limitOffset !== 0 ?
          `${limitRef}${
            limitRef ?
              epex.limitOffset > 0 ?
                "+"
              : "-"
            : ""
          }${round(Math.abs(epex.limitOffset), 6)}`
        : limitRef;
      return `${obs} h ${limit}`.trim();
    }
    case ConditionBlockType.TIMEREFERENCE: {
      // references come in start/end pairs, one pair per range
      const nRanges = Math.floor(
        ((block as { references?: IReference[] }).references?.length ?? 0) / 2,
      );
      return nRanges === 1 ? "1 range" : `${nRanges} ranges`;
    }
    case ConditionBlockType.ERROR: {
      const error = block as { alarmSource?: string; references?: IReference[] };
      const source = ALARM_SOURCES[error.alarmSource ?? ""] ?? "Any Alarm";
      const ref = getLongRefText(error.references?.[0], parameter, false);
      return ref ? `${source} ${ref}` : source;
    }
    case ConditionBlockType.CONDITION:
      return getLongRefText(
        (block as { references?: IReference[] }).references?.[0],
        parameter,
        true,
      );
    default:
      return "";
  }
}

const ALARM_SOURCES: Record<string, string> = {
  NVAlarm_Targetvalue: "Target",
  NVAlarm_Restart: "Restart",
  NVAlarm_MissingSync: "Sync",
  NVAlarm_EnergyMeter: "Energy Meter",
  NVAlarm_Modul: "Module",
  NVAlarm_ManualActive: "IO Override",
  NVAlarm_LINX: "Controller",
  NVAlarm_Custom: "Custom Alarm",
};

// shows the first set dimension (day/month/year) of the first range, mirroring enplan
function formatCalendarRanges(ranges: IConditionBlockCalendarRange[]): string {
  for (const range of ranges) {
    if (range.startDay !== 0 && range.endDay !== 0) {
      const text =
        range.startDay !== range.endDay ?
          `${range.startDay}.-${range.endDay}.`
        : `${range.startDay}.`;
      return ranges.length > 1 ? `${text},...` : text;
    }
    if (range.startMonth !== 0 && range.endMonth !== 0) {
      const text =
        range.startMonth !== range.endMonth ?
          `${getMonthName(range.startMonth)}-${getMonthName(range.endMonth)}`
        : getMonthName(range.startMonth);
      return ranges.length > 1 ? `${text},...` : text;
    }
    if (range.startYear !== 0 && range.endYear !== 0) {
      const text =
        range.startYear !== range.endYear ?
          `${range.startYear}-${range.endYear}`
        : `${range.startYear}`;
      return ranges.length > 1 ? `${text},...` : text;
    }
  }
  return "";
}

function formatWeekdayRanges(ranges: IConditionBlockWeekdayRange[]): string {
  for (const range of ranges) {
    if (range.startWeekday !== 0) {
      const text =
        range.startWeekday !== range.endWeekday ?
          `${getWeekdayName(range.startWeekday)}-${getWeekdayName(range.endWeekday)}`
        : getWeekdayName(range.startWeekday);
      return ranges.length > 1 ? `${text},...` : text;
    }
    if (range.startWeek !== 0) {
      const text =
        range.startWeek !== range.endWeek ?
          `CW ${range.startWeek}-${range.endWeek}`
        : `CW ${range.startWeek}`;
      return ranges.length > 1 ? `${text},...` : text;
    }
  }
  return "";
}

function formatClock(seconds: number): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(Math.floor(seconds / 3600))}:${pad(Math.floor((seconds / 60) % 60))}`;
}

function formatDaytime(
  ranges: { startTime: number; endTime: number }[],
  startMode: ConditionBlockTimeMode | undefined,
  endMode: ConditionBlockTimeMode | undefined,
): string {
  const astro = (mode: ConditionBlockTimeMode | undefined) =>
    mode === ConditionBlockTimeMode.SUNRISE || mode === ConditionBlockTimeMode.SUNSET;
  if (astro(startMode) || astro(endMode)) {
    return "sun";
  }
  if (ranges.length === 0) {
    return "";
  }
  const text = `${formatClock(ranges[0].startTime)}-${formatClock(ranges[0].endTime)}`;
  return ranges.length > 1 ? `${text},...` : text;
}

// blocks producing an analog (non-digital) output use a "-" invert prefix, the rest "!"
const ANALOG_BLOCK_TYPES = new Set<ConditionBlockType>([
  ConditionBlockType.CONSTANT,
  ConditionBlockType.VALUE,
  ConditionBlockType.PID,
  ConditionBlockType.CURVE,
]);

// block types whose engine dataValue is not a meaningful reading; these keep their static
// parameter caption in live view instead of showing a live value, matching enplan: CALENDAR/
// DAYTIME/WEEKDAY/TIMEREFERENCE always report 0/false, CONSTANT's value never changes, and
// SEQUENCE's active period is shown as its static list rather than a raw period index
const NO_LIVE_CAPTION_TYPES = new Set<ConditionBlockType>([
  ConditionBlockType.CALENDAR,
  ConditionBlockType.DAYTIME,
  ConditionBlockType.WEEKDAY,
  ConditionBlockType.TIMEREFERENCE,
  ConditionBlockType.CONSTANT,
  ConditionBlockType.SEQUENCE,
]);

// sentinel the engine reports as dataValue for MINOFF/MAXOFF/MINON/MAXON/PWM/PRIORITY while no
// timer is running, i.e. "not applicable" rather than an actual reading
const NO_TIMER_VALUE = 0xffff;

// per-type formatting of a block's live dataValue, mirroring enplan's live condition view:
// most block types just show the plain rounded number, but a few decode it (regulation level,
// mm:ss/hh:mm:ss durations, kW unit)
function formatLiveValue(type: ConditionBlockType, value: number): string {
  switch (type) {
    case ConditionBlockType.PRIORITY:
      return getRegulationLevelText(value);
    case ConditionBlockType.MINOFF:
    case ConditionBlockType.MINON:
      return formatMinutesSeconds(value);
    case ConditionBlockType.MAXOFF:
    case ConditionBlockType.MAXON:
    case ConditionBlockType.PWM:
      return formatSeconds(value);
    case ConditionBlockType.POWER:
      return `${round(value, 3)} kW`;
    default:
      return String(round(value, 3));
  }
}

const STATUS_ON_COLOR = "#198754";
const STATUS_OFF_COLOR = "#dc3545";
const STATUS_NEUTRAL_COLOR = "#888";

export default class ConditionGraph extends React.Component<IConditionGraphProps> {
  // build the full node list including the implicit start/end blocks at index 0/1
  private getNodes(): IGraphNode[] {
    const { condition } = this.props;
    const nodes: IGraphNode[] = [
      { type: ConditionBlockType.START, position: condition.startPos },
      { type: ConditionBlockType.END, position: condition.endPos },
    ];
    condition.blocks.forEach((block) =>
      nodes.push({ type: block.type, position: block.position, block }),
    );
    return nodes;
  }

  public render() {
    const { showLive } = this.props;
    const nodes = this.getNodes();
    const positions = nodes.map((node) => decodePosition(node.position));
    const statuses = nodes.map((node, nodeIndex) => this.getNodeStatus(node, nodeIndex));
    const edgeWeights = nodes.map((node, nodeIndex) => this.getEdgeWeight(node, nodeIndex));
    // connection labels (the live weight carried by each edge) are only shown for conditions
    // that actually carry analog values; a purely boolean graph would just label everything "1"
    const showLabels =
      Boolean(showLive) && edgeWeights.some((value) => isSet(value) && value !== 0 && value !== 1);
    const maxX = Math.max(0, ...positions.map((curPos) => curPos.x));
    const maxY = Math.max(0, ...positions.map((curPos) => curPos.y));
    const width = (maxX + 1) * GRID_SIZE_X + CANVAS_MARGIN;
    const height = (maxY + 1) * GRID_SIZE_Y + ICON_SIZE;

    return (
      <div className="conditiongraph" style={{ position: "relative", width, height }}>
        <svg
          width={width}
          height={height}
          style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
        >
          {this.renderConnections(positions, edgeWeights, showLabels)}
        </svg>
        {nodes.map((node, nodeIndex) =>
          this.renderNode(node, positions[nodeIndex], nodeIndex, statuses[nodeIndex]),
        )}
      </div>
    );
  }

  private renderConnections(
    positions: { x: number; y: number }[],
    edgeWeights: (number | undefined)[],
    showLabels: boolean,
  ) {
    const elements: JSX.Element[] = [];
    this.props.condition.adjacencyMatrix.forEach((row, srcIndex) => {
      row.forEach((isConnected, destIndex) => {
        const srcPos = positions[srcIndex];
        const destPos = positions[destIndex];
        if (!isConnected || !srcPos || !destPos) {
          return;
        }
        const x1 = srcPos.x * GRID_SIZE_X + CANVAS_MARGIN + BODY_RIGHT_EDGE;
        const y1 = srcPos.y * GRID_SIZE_Y + BODY_HEIGHT / 2;
        const x2 = destPos.x * GRID_SIZE_X + CANVAS_MARGIN + BODY_OFFSET;
        const y2 = destPos.y * GRID_SIZE_Y + BODY_HEIGHT / 2;
        // an edge carries the live weight leaving its source node
        const weight = edgeWeights[srcIndex];
        const color =
          isSet(weight) ?
            weight ? STATUS_ON_COLOR
            : STATUS_OFF_COLOR
          : STATUS_NEUTRAL_COLOR;
        elements.push(
          <line
            key={`${srcIndex}-${destIndex}`}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={color}
            strokeWidth={2}
            strokeDasharray={isSet(weight) ? "4" : undefined}
          />,
        );
        if (showLabels && isSet(weight)) {
          elements.push(
            <text
              key={`${srcIndex}-${destIndex}-label`}
              x={(x1 + x2) / 2}
              y={(y1 + y2) / 2 - 4}
              textAnchor="middle"
              fontSize={11}
              fill={color}
            >
              {round(weight, 3)}
            </text>,
          );
        }
      });
    });
    return elements;
  }

  // caption shown below the icon: the live value where available, otherwise the static
  // parameter caption (see getStaticCaption)
  private getCaption(node: IGraphNode, nodeIndex: number): string {
    if (node.type === ConditionBlockType.START || node.type === ConditionBlockType.END) {
      return this.getStaticCaption(node);
    }
    return this.getLiveCaption(node, nodeIndex) ?? this.getStaticCaption(node);
  }

  // the parameter-view caption, i.e. the block's configuration regardless of live data; always
  // used for the hover tooltip so it stays available even while the live value is shown instead
  private getStaticCaption(node: IGraphNode): string {
    if (node.type === ConditionBlockType.START) {
      return "";
    }
    if (node.type === ConditionBlockType.END) {
      return `${this.props.condition.onDelay}/${this.props.condition.offDelay}`;
    }
    return getBlockCaption(node.block!, this.props.parameter);
  }

  // live value shown in place of the caption, i.e. the block's underlying measured quantity;
  // undefined falls back to the static parameter caption (e.g. calendar/weekday blocks, or an
  // inactive timer's sentinel value)
  private getLiveCaption(node: IGraphNode, nodeIndex: number): string | undefined {
    const { showLive, dataValue } = this.props;
    if (!showLive || !dataValue || NO_LIVE_CAPTION_TYPES.has(node.type)) {
      return undefined;
    }
    // nodes are [start, end, ...blocks], so block index in condition.blocks is nodeIndex - 2
    const value = dataValue[nodeIndex - 2];
    if (!isSet(value) || !Number.isFinite(value) || value === NO_TIMER_VALUE) {
      return undefined;
    }
    return formatLiveValue(node.type, value);
  }

  // current live status of a node, used for its border color: the block's own engine-computed
  // result (independent of whether it is actually reached, e.g. blocked by an earlier false
  // block in series), 1 for the start node, or the condition's overall result for the end node;
  // only set while showing the live view with a finite value available. NaN (e.g. a block type
  // the engine doesn't report a real result for) is treated the same as "no data", not as
  // falsy/off, so it renders with a neutral border and gray connection lines
  private getNodeStatus(node: IGraphNode, nodeIndex: number): number | undefined {
    const { showLive, blockResult, connectionStatus } = this.props;
    if (!showLive) {
      return undefined;
    }
    let value: number | undefined;
    if (node.type === ConditionBlockType.START) {
      value = connectionStatus?.[0];
    } else if (node.type === ConditionBlockType.END) {
      value = connectionStatus?.[connectionStatus.length - 1];
    } else {
      // nodes are [start, end, ...blocks], so block index in condition.blocks is nodeIndex - 2
      value = blockResult?.[nodeIndex - 2];
    }
    return isSet(value) && Number.isFinite(value) ? value : undefined;
  }

  // live weight carried on a node's outgoing edges: the weight arriving at the node (its
  // connectionStatus, i.e. the accumulated result of everything upstream) multiplied by the
  // node's own value. This can differ substantially from the node's own value alone whenever an
  // earlier analog block scaled the signal, e.g. an upstream factor of -0.22 turns a constant
  // block's raw value of 200 into an outgoing edge weight of -44. The end node has no outgoing
  // edges and the start node's own value is always 1, so both just use their connectionStatus
  private getEdgeWeight(node: IGraphNode, nodeIndex: number): number | undefined {
    const { showLive, blockResult, connectionStatus } = this.props;
    if (!showLive || !connectionStatus || node.type === ConditionBlockType.END) {
      return undefined;
    }
    if (node.type === ConditionBlockType.START) {
      const value = connectionStatus[0];
      return isSet(value) && Number.isFinite(value) ? value : undefined;
    }
    // engine order is [start, ...blocks, end]; the weight arriving at a block sits one slot
    // earlier there than in the [start, end, ...blocks] node order used here
    const incoming = connectionStatus[nodeIndex - 1];
    const ownValue = blockResult?.[nodeIndex - 2];
    if (
      !isSet(incoming) ||
      !isSet(ownValue) ||
      !Number.isFinite(incoming) ||
      !Number.isFinite(ownValue)
    ) {
      return undefined;
    }
    return incoming * ownValue;
  }

  private renderNode(
    node: IGraphNode,
    position: { x: number; y: number },
    nodeIndex: number,
    status: number | undefined,
  ) {
    // comment blocks (type FALSE) sit on the grid but have no icon; their text may contain
    // html and is allowed to grow wider than a normal icon cell
    if (node.type === ConditionBlockType.FALSE) {
      const comment = (node.block as IConditionBlockComment).comment ?? "";
      return (
        <div
          key={nodeIndex}
          className="conditiongraph-comment"
          style={{
            position: "absolute",
            left: position.x * GRID_SIZE_X + CANVAS_MARGIN,
            top: position.y * GRID_SIZE_Y,
            width: COMMENT_WIDTH,
          }}
          // comment text is author-provided condition config and may contain html (e.g. <b>)
          dangerouslySetInnerHTML={{ __html: comment }}
        />
      );
    }
    const Icon = getBlockIcon(node.type);
    const caption = this.getCaption(node, nodeIndex);
    const invertPrefix =
      node.block?.invert ?
        ANALOG_BLOCK_TYPES.has(node.type) ?
          "-"
        : "!"
      : "";
    const statusClass =
      isSet(status) ? `conditiongraph-status-${status ? "on" : "off"}` : undefined;

    return (
      <div
        key={nodeIndex}
        className="conditiongraph-block"
        style={{
          position: "absolute",
          left: position.x * GRID_SIZE_X + CANVAS_MARGIN,
          top: position.y * GRID_SIZE_Y,
          width: ICON_SIZE,
        }}
        title={`${invertPrefix}${this.getStaticCaption(node)}`}
      >
        {/* the status border stays close to icon-width (matching the connection lines' anchor
            point) but is tall enough to cover the caption/value shown below the icon */}
        <div
          className={["conditiongraph-body", statusClass].filter(isSet).join(" ")}
          style={{ width: BODY_WIDTH, marginLeft: BODY_OFFSET, height: BODY_HEIGHT }}
        >
          <Icon
            width={ICON_SIZE}
            height={ICON_SIZE}
            style={{ display: "block", margin: "0 auto" }}
          />
          <div
            className="conditiongraph-caption"
            style={{ width: GRID_SIZE_X, marginLeft: (BODY_WIDTH - GRID_SIZE_X) / 2 }}
          >
            {invertPrefix}
            {caption}
          </div>
        </div>
        {node.type === ConditionBlockType.END && isSet(status) && (
          <div
            className={`conditiongraph-endvalue conditiongraph-endvalue-${status ? "on" : "off"}`}
            style={{ left: BODY_RIGHT_EDGE + END_VALUE_GAP, height: BODY_HEIGHT }}
          >
            {round(status, 3)}
          </div>
        )}
      </div>
    );
  }
}
